import {
	type Cell,
	flexRender,
	type Row,
	type RowSelectionState,
	type Table,
} from "@tanstack/react-table";
import {
	defaultRangeExtractor,
	type Range,
	useVirtualizer,
} from "@tanstack/react-virtual";
import { ChevronRight } from "lucide-react";
import {
	type CSSProperties,
	forwardRef,
	memo,
	type ReactNode,
	useCallback,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { DataTableResizeHandle } from "@/shared/components/ui/data-table-resize-handle";
import { bisectRight } from "@/shared/utils/bisect";
import { formatUnits } from "@/shared/utils/formatUnits";
import { Skeleton } from "@/v2/components/ui/skeleton";
import { cn } from "@/v2/lib/utils";
import { getDateHourLabel, getGroupLabel, NARRATIVE_MIN_PX } from "./columns";
import { EntryProcessingRows } from "./EntryProcessingRows";
import type { Entry } from "./normalize";
import { SelectionProvider, useSelectionAPI } from "./selection";
import type { Timekeeper } from "./timekeepers";

// Real trailing grid track for the ColumnsMenu button. Keeping it in the same
// grid as the headers/body prevents the button from overlaying Units, while the
// sticky header cell keeps the menu visible during horizontal scroll.
const COLUMN_MENU_TRACK_PX = 32;
const SKELETON_ROW_COUNT = 10;
const GROUP_ROW_ESTIMATE = 28;
const DATA_ROW_ESTIMATE = 63;

interface EntriesTableProps {
	table: Table<Entry>;
	isLoading: boolean;
	rowSelection: RowSelectionState;
	onRowClick?: (entry: Entry) => void;
	exportedRowsInert?: boolean;
	emptyMessage?: string;
	timekeepers?: readonly Timekeeper[] | null;
	columnsMenu?: ReactNode;
}

export function EntriesTable({
	table,
	isLoading,
	rowSelection,
	onRowClick,
	exportedRowsInert = true,
	emptyMessage = "No entries yet.",
	timekeepers = null,
	columnsMenu = null,
}: EntriesTableProps) {
	const rows = table.getRowModel().rows;
	const leafRows = useMemo(
		() => rows.filter((row) => !row.getIsGrouped()),
		[rows],
	);
	const hasData = table.getCoreRowModel().rows.length > 0;

	// Build the grid-template-columns string. Subscribes to columnSizing
	// (committed widths) only — `columnResizeMode: "onEnd"` means sizes don't
	// move during drag, so we deliberately don't subscribe to columnSizingInfo
	// here. The drag indicator is handled inside DataTableResizeHandle.
	//
	// Every column is fixed-width except the narrative, which is the flex
	// absorber: its track is `minmax(NARRATIVE_MIN_PX, 1fr)`, so it soaks up any
	// space left after the fixed columns. A trailing fixed menu track reserves
	// room for the ColumnsMenu button. `minWidth` (`totalWidth` below) is the
	// minimum total content width — the fixed columns plus narrative's floor plus
	// the menu track — so the table fills the viewport when there's slack and
	// scrolls horizontally, with narrative pinned at its floor, when there isn't.
	const columnSizing = table.getState().columnSizing;
	const columnVisibility = table.getState().columnVisibility;
	const columnOrder = table.getState().columnOrder;
	// biome-ignore lint/correctness/useExhaustiveDependencies: deps are state subscriptions
	const { gridTemplate, totalWidth } = useMemo(() => {
		const tracks: string[] = [];
		let width = 0;
		for (const col of table.getVisibleLeafColumns()) {
			if (col.id === "description") {
				tracks.push(`minmax(${NARRATIVE_MIN_PX}px, 1fr)`);
				width += NARRATIVE_MIN_PX;
			} else {
				const size = col.getSize();
				tracks.push(`${size}px`);
				width += size;
			}
		}
		tracks.push(`${COLUMN_MENU_TRACK_PX}px`);
		width += COLUMN_MENU_TRACK_PX;
		return { gridTemplate: tracks.join(" "), totalWidth: width };
	}, [table, columnSizing, columnVisibility, columnOrder]);

	// Push the template through a CSS variable on the root: each row's inline
	// style references `var(--entries-cols)`, so a size change updates one
	// custom property on the root and the browser relays out — no React
	// re-render of body cells.
	const rootStyle = useMemo(
		() =>
			({
				"--entries-cols": gridTemplate,
				"--entries-width": `${totalWidth}px`,
			}) as CSSProperties,
		[gridTemplate, totalWidth],
	);
	const rowStyle = useMemo(
		() =>
			({
				gridTemplateColumns: "var(--entries-cols)",
			}) as CSSProperties,
		[],
	);
	// TanStack applies columnOrder to header/leaf columns. Row.getVisibleCells()
	// intentionally remains in definition order, so body cells must be projected
	// through the visible leaf column ids to stay aligned with reordered headers.
	// biome-ignore lint/correctness/useExhaustiveDependencies: columnVisibility/columnOrder are table-state subscriptions; the table object is stable.
	const visibleColumnIds = useMemo(
		() => table.getVisibleLeafColumns().map((col) => col.id),
		[table, columnVisibility, columnOrder],
	);
	const visibleColumnKey = visibleColumnIds.join("|");
	const scrollerRef = useRef<HTMLDivElement>(null);
	const headerScrollerRef = useRef<HTMLDivElement>(null);
	const [scrollbarWidth, setScrollbarWidth] = useState(0);

	useLayoutEffect(() => {
		const scroller = scrollerRef.current;
		if (!scroller) return;

		const updateScrollbarWidth = () => {
			setScrollbarWidth(scroller.offsetWidth - scroller.clientWidth);
		};

		updateScrollbarWidth();
		const resizeObserver = new ResizeObserver(updateScrollbarWidth);
		resizeObserver.observe(scroller);
		window.addEventListener("resize", updateScrollbarWidth);
		return () => {
			resizeObserver.disconnect();
			window.removeEventListener("resize", updateScrollbarWidth);
		};
	}, []);

	// Header + body spacer stretch to the full column run (or the viewport,
	// whichever is wider) so they stay aligned with the rows during scroll.
	const headerFullWidthStyle: CSSProperties = {
		gridTemplateColumns: "var(--entries-cols)",
		width: scrollbarWidth > 0 ? `calc(100% - ${scrollbarWidth}px)` : "100%",
		minWidth: "var(--entries-width)",
	};

	const lastScrollLeftRef = useRef(0);
	const handleBodyScroll = useCallback(
		(event: React.UIEvent<HTMLDivElement>) => {
			const { scrollLeft } = event.currentTarget;
			if (scrollLeft === lastScrollLeftRef.current) return;
			lastScrollLeftRef.current = scrollLeft;
			if (headerScrollerRef.current) {
				headerScrollerRef.current.scrollLeft = scrollLeft;
			}
		},
		[],
	);
	const toggleGroup = useCallback(
		(row: Row<Entry>) => {
			table.setExpanded((previous) => {
				if (previous === true) {
					const next: Record<string, boolean> = {};
					for (const preExpandedRow of table.getPreExpandedRowModel()
						.flatRows) {
						if (preExpandedRow.getIsGrouped() && preExpandedRow.id !== row.id) {
							next[preExpandedRow.id] = true;
						}
					}
					return next;
				}

				const next = { ...(previous ?? {}) };
				if (next[row.id]) delete next[row.id];
				else next[row.id] = true;
				return next;
			});
		},
		[table],
	);

	return (
		<SelectionProvider
			orderedRows={leafRows}
			selection={rowSelection}
			setSelection={table.setRowSelection}
		>
			<div
				style={rootStyle}
				className="relative flex h-full w-full flex-col bg-background"
			>
				<div
					ref={headerScrollerRef}
					className="shrink-0 overflow-hidden bg-muted border-b border-border"
				>
					<div className="grid" style={headerFullWidthStyle}>
						{table.getHeaderGroups().map((hg) =>
							hg.headers.map((h) => (
								<div
									key={h.id}
									className="relative flex h-10 min-w-0 items-center overflow-hidden px-3 text-xs"
								>
									{h.isPlaceholder
										? null
										: flexRender(h.column.columnDef.header, h.getContext())}
									<DataTableResizeHandle header={h} />
								</div>
							)),
						)}
						<div className="sticky right-0 z-20 flex h-10 items-center justify-center bg-muted">
							{columnsMenu}
						</div>
					</div>
				</div>

				{/* Optimistic "processing" rows pinned directly under the column
					headers, above the virtualized body. Kept outside the scroller so
					they don't perturb the virtualizer's visible-range math. */}
				<EntryProcessingRows />

				<div
					ref={scrollerRef}
					className="min-h-0 flex-1 overflow-auto"
					onScroll={handleBodyScroll}
				>
					{isLoading && !hasData ? (
						<EntriesTableLoadingSkeleton
							rowStyle={rowStyle}
							visibleColumnIds={visibleColumnIds}
						/>
					) : rows.length === 0 ? (
						<div className="h-24 flex items-center justify-center text-sm text-muted-foreground">
							{emptyMessage}
						</div>
					) : (
						<VirtualizedTableBody
							rows={rows}
							scrollerRef={scrollerRef}
							rowStyle={rowStyle}
							visibleColumnIds={visibleColumnIds}
							visibleColumnKey={visibleColumnKey}
							onRowClick={onRowClick}
							exportedRowsInert={exportedRowsInert}
							timekeepers={timekeepers}
							toggleGroup={toggleGroup}
						/>
					)}
				</div>
			</div>
		</SelectionProvider>
	);
}

function VirtualizedTableBody({
	rows,
	scrollerRef,
	rowStyle,
	visibleColumnIds,
	visibleColumnKey,
	onRowClick,
	exportedRowsInert,
	timekeepers,
	toggleGroup,
}: {
	rows: Row<Entry>[];
	scrollerRef: React.RefObject<HTMLDivElement | null>;
	rowStyle: CSSProperties;
	visibleColumnIds: string[];
	visibleColumnKey: string;
	onRowClick?: (entry: Entry) => void;
	exportedRowsInert: boolean;
	timekeepers: readonly Timekeeper[] | null;
	toggleGroup: (row: Row<Entry>) => void;
}) {
	// Keep the virtualizer low in the tree, matching TanStack's virtualized-rows
	// pattern, so scroll updates do not re-render the whole table shell.
	const stickyIndexes = useMemo(
		() =>
			rows
				.map((row, index) => (row.getIsGrouped() ? index : -1))
				.filter((index) => index !== -1),
		[rows],
	);

	const findLastStickyIndexAtOrBefore = useCallback(
		(index: number) => {
			const insertionIndex = bisectRight(stickyIndexes, index);
			return insertionIndex === 0
				? null
				: (stickyIndexes[insertionIndex - 1] ?? null);
		},
		[stickyIndexes],
	);

	const rangeExtractor = useCallback(
		(range: Range) => {
			// Custom range extraction exists only to keep the current grouped row
			// mounted after it scrolls above the window, preserving sticky headers.
			const activeStickyIndex = findLastStickyIndexAtOrBefore(range.startIndex);
			const defaultIndexes = defaultRangeExtractor(range);

			if (activeStickyIndex == null) return defaultIndexes;

			const firstDefaultIndex = defaultIndexes[0];
			return firstDefaultIndex != null && activeStickyIndex >= firstDefaultIndex
				? defaultIndexes
				: [activeStickyIndex, ...defaultIndexes];
		},
		[findLastStickyIndexAtOrBefore],
	);

	const rowVirtualizer = useVirtualizer<HTMLDivElement, HTMLDivElement>({
		count: rows.length,
		getScrollElement: () => scrollerRef.current,
		estimateSize: (index) =>
			rows[index]?.getIsGrouped() ? GROUP_ROW_ESTIMATE : DATA_ROW_ESTIMATE,
		getItemKey: (index) => rows[index]?.id ?? index,
		// Entry row heights vary because narratives and matter details can wrap.
		// Dynamic measurement keeps scrollbar size and scroll positions accurate.
		measureElement: (element) => element?.getBoundingClientRect().height ?? 0,
		overscan: 8,
		rangeExtractor,
	});
	const virtualItems = rowVirtualizer.getVirtualItems();
	const activeStickyIndex =
		rowVirtualizer.range == null
			? null
			: findLastStickyIndexAtOrBefore(rowVirtualizer.range.startIndex);

	return (
		<div
			className="relative w-full min-w-[var(--entries-width)]"
			style={{ height: rowVirtualizer.getTotalSize() }}
		>
			{virtualItems.map((virtualRow) => {
				const row = rows[virtualRow.index];
				if (!row) return null;

				const isActiveSticky =
					row.getIsGrouped() && activeStickyIndex === virtualRow.index;
				const virtualRowClassName = isActiveSticky
					? "sticky top-0 z-20 w-full"
					: "absolute top-0 left-0 w-full";
				const virtualRowStyle: CSSProperties | undefined = isActiveSticky
					? undefined
					: { transform: `translateY(${virtualRow.start}px)` };

				if (row.getIsGrouped()) {
					const groupMeta = getGroupRowMeta(row, timekeepers);
					return (
						<div
							key={`g:${virtualRow.key}`}
							data-index={virtualRow.index}
							ref={rowVirtualizer.measureElement}
							style={virtualRowStyle}
							className={virtualRowClassName}
						>
							<GroupRow
								rowId={row.id}
								label={groupMeta.label}
								unitsTotal={groupMeta.unitsTotal}
								isExpanded={row.getIsExpanded()}
								onToggle={() => toggleGroup(row)}
							/>
						</div>
					);
				}

				return (
					<div
						key={`r:${virtualRow.key}`}
						data-index={virtualRow.index}
						ref={rowVirtualizer.measureElement}
						style={virtualRowStyle}
						className={virtualRowClassName}
					>
						<DataRow
							row={row}
							rowStyle={rowStyle}
							isSelected={row.getIsSelected()}
							visibleColumnIds={visibleColumnIds}
							visibleColumnKey={visibleColumnKey}
							onRowClick={onRowClick}
							exportedRowsInert={exportedRowsInert}
						/>
					</div>
				);
			})}
		</div>
	);
}

function EntriesTableLoadingSkeleton({
	rowStyle,
	visibleColumnIds,
}: {
	rowStyle: CSSProperties;
	visibleColumnIds: string[];
}) {
	return (
		<div
			aria-busy="true"
			className="w-full"
			style={{ minWidth: "var(--entries-width)" }}
		>
			<output className="sr-only">Loading entries…</output>
			<div className="flex items-center justify-between border-b border-border bg-muted/40 px-3 py-1.5">
				<Skeleton className="h-3 w-40" />
				<Skeleton className="h-3 w-20" />
			</div>
			{Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
				<div
					// biome-ignore lint/suspicious/noArrayIndexKey: fixed-count placeholder rows have no data identity.
					key={index}
					className="grid border-b border-border"
					style={rowStyle}
				>
					{visibleColumnIds.map((columnId) => (
						<div
							key={columnId}
							className="flex min-w-0 flex-col justify-center px-3 py-2"
						>
							<EntriesTableSkeletonCell columnId={columnId} rowIndex={index} />
						</div>
					))}
				</div>
			))}
		</div>
	);
}

function EntriesTableSkeletonCell({
	columnId,
	rowIndex,
}: {
	columnId: string;
	rowIndex: number;
}) {
	const narrativeWidth = ["w-11/12", "w-3/4", "w-5/6"][rowIndex % 3];
	const secondaryWidth = ["w-20", "w-28", "w-16"][rowIndex % 3];

	switch (columnId) {
		case "_select":
			return <Skeleton className="mx-auto size-4 rounded-sm" />;
		case "timekeeper":
			return <Skeleton className="h-3 w-20" />;
		case "application":
			return <Skeleton className="mx-auto size-7 rounded-md" />;
		case "date":
			return (
				<div className="space-y-1.5">
					<Skeleton className="h-3 w-16" />
					<Skeleton className="h-3 w-12" />
				</div>
			);
		case "matter":
			return (
				<div className="space-y-1.5">
					<Skeleton className="h-3 w-24" />
					<Skeleton className="h-3 w-4/5" />
					<Skeleton className={cn("h-3", secondaryWidth)} />
				</div>
			);
		case "codes":
			return (
				<div className="mx-auto space-y-1.5">
					<Skeleton className="h-3 w-10" />
					<Skeleton className="h-3 w-12" />
				</div>
			);
		case "description":
			return (
				<div className="space-y-1.5">
					<Skeleton className={cn("h-3", narrativeWidth)} />
					<Skeleton className="h-3 w-full" />
					{rowIndex % 3 === 0 ? <Skeleton className="h-3 w-2/3" /> : null}
				</div>
			);
		case "status":
		case "billableCategory":
			return <Skeleton className="mx-auto size-5 rounded-full" />;
		case "units":
			return <Skeleton className="mx-auto h-7 w-12 rounded-[7px]" />;
		default:
			return <Skeleton className="h-3 w-3/4" />;
	}
}

function getGroupRowMeta(
	row: Row<Entry>,
	timekeepers: readonly Timekeeper[] | null,
): {
	label: string;
	unitsTotal?: number;
} {
	const columnId = row.groupingColumnId;
	const leafRows = row.getLeafRows();
	const firstEntry = leafRows[0]?.original;
	const label =
		!columnId || !firstEntry
			? String(row.groupingValue ?? "Unknown")
			: columnId === "date"
				? getDateHourLabel(firstEntry)
				: getGroupLabel(firstEntry, columnId, timekeepers) ||
					String(row.groupingValue ?? "Unknown");
	const unitsTotal =
		columnId === "matter" ? row.getValue<number>("units") : undefined;
	return {
		label,
		unitsTotal:
			typeof unitsTotal === "number" && Number.isFinite(unitsTotal)
				? unitsTotal
				: undefined,
	};
}

type GroupRowProps = {
	rowId: string;
	label: string;
	unitsTotal?: number;
	isExpanded: boolean;
	onToggle: () => void;
};

const GroupRow = memo(
	function GroupRow({
		label,
		unitsTotal,
		isExpanded,
		onToggle,
	}: GroupRowProps) {
		return (
			<button
				type="button"
				aria-expanded={isExpanded}
				onClick={onToggle}
				className="flex w-full items-center gap-3 border-b border-border bg-muted/95 px-3 py-1.5 text-left text-xs font-medium text-muted-foreground backdrop-blur-sm transition-colors hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-[-2px]"
			>
				<span className="flex min-w-0 items-center gap-1.5">
					<ChevronRight
						className={cn(
							"size-3.5 shrink-0 transition-transform duration-150 ease-out",
							isExpanded && "rotate-90",
						)}
					/>
					<span className="truncate">{label}</span>
				</span>
				{unitsTotal !== undefined ? (
					<span className="ml-auto shrink-0 tabular-nums text-foreground">
						{formatUnits(unitsTotal)} units
					</span>
				) : null}
			</button>
		);
	},
	(prev, next) =>
		prev.rowId === next.rowId &&
		prev.label === next.label &&
		prev.unitsTotal === next.unitsTotal &&
		prev.isExpanded === next.isExpanded,
);

const DataRow = memo(
	function DataRow({
		row,
		rowStyle,
		onRowClick,
		visibleColumnIds,
		exportedRowsInert,
	}: {
		row: Row<Entry>;
		rowStyle: CSSProperties;
		isSelected: boolean;
		visibleColumnIds: string[];
		visibleColumnKey: string;
		onRowClick?: (entry: Entry) => void;
		exportedRowsInert: boolean;
	}) {
		const cellsByColumnId = new Map<string, Cell<Entry, unknown>>(
			row.getAllCells().map((cell) => [cell.column.id, cell]),
		);
		return (
			<InteractiveRow
				row={row}
				rowStyle={rowStyle}
				onRowClick={onRowClick}
				exportedRowsInert={exportedRowsInert}
			>
				{visibleColumnIds.map((columnId) => {
					const cell = cellsByColumnId.get(columnId);
					if (!cell) return null;
					return (
						<div
							key={cell.id}
							className="flex flex-col justify-center px-3 py-2 text-sm min-w-0"
						>
							{flexRender(cell.column.columnDef.cell, cell.getContext())}
						</div>
					);
				})}
			</InteractiveRow>
		);
	},
	(prev, next) =>
		prev.row === next.row &&
		prev.isSelected === next.isSelected &&
		prev.visibleColumnKey === next.visibleColumnKey &&
		prev.rowStyle === next.rowStyle &&
		prev.onRowClick === next.onRowClick &&
		prev.exportedRowsInert === next.exportedRowsInert,
);

// Row body. Pulls handleSelect from context so the row click matches the
// checkbox: ctrl/cmd toggles, shift extends a range, plain click replaces.
// shiftKey on mousedown gets preventDefault'd to suppress the browser's
// native text-range selection flash during a shift-click.
const InteractiveRow = forwardRef<
	HTMLDivElement,
	{
		row: Row<Entry>;
		rowStyle: CSSProperties;
		children: ReactNode;
		onRowClick?: (entry: Entry) => void;
		exportedRowsInert: boolean;
	} & React.HTMLAttributes<HTMLDivElement>
>(function InteractiveRow(
	{ row, rowStyle, children, onRowClick, exportedRowsInert, ...rest },
	ref,
) {
	const { handleSelect } = useSelectionAPI();
	const isExported = exportedRowsInert && row.original.status === "exported";
	// Exported entries are terminal AND inert — see the onClick below. Every
	// other status (tracked, reviewed, deleted) is a normal, editable row.
	return (
		// biome-ignore lint/a11y/useSemanticElements: virtualized CSS grid rows cannot use table markup here.
		<div
			{...rest}
			ref={ref}
			role="row"
			tabIndex={isExported ? -1 : 0}
			data-state={row.getIsSelected() ? "selected" : undefined}
			data-edit-locked={isExported ? "true" : undefined}
			className={cn(
				"grid border-b border-border transition-colors",
				"hover:bg-muted/50 data-[state=selected]:bg-muted/30",
			)}
			style={rowStyle}
			onMouseDown={(e) => {
				if (e.shiftKey) e.preventDefault();
				rest.onMouseDown?.(e);
			}}
			onClick={(e) => {
				// Exported rows are inert: a body click neither opens the
				// (disabled) edit dialog nor toggles selection. Selection for an
				// exported row is exclusively via the checkbox column
				// (SelectCheckboxCell, which stops propagation).
				if (isExported) {
					rest.onClick?.(e);
					return;
				}
				// Modifier-key gestures fall through to selection — the only branch
				// that opens the edit dialog is the bare-click on an editable row.
				if (e.ctrlKey || e.metaKey || e.shiftKey || !onRowClick) {
					handleSelect(row.id, e);
				} else {
					onRowClick(row.original);
				}
				rest.onClick?.(e);
			}}
			onKeyDown={(e) => {
				if (isExported) {
					rest.onKeyDown?.(e);
					return;
				}
				if (e.key === "Enter" && onRowClick) {
					e.preventDefault();
					onRowClick(row.original);
				} else if (e.key === " ") {
					e.preventDefault();
					handleSelect(
						row.id,
						{ ctrlKey: true, metaKey: true, shiftKey: e.shiftKey },
						{ treatAsAdditive: true },
					);
				}
				rest.onKeyDown?.(e);
			}}
		>
			{children}
		</div>
	);
});
